/**
 * Staggered list entrance — items cascade in with a per-index delay.
 *
 * GPU-only: the animation itself is the `.motion-item-enter` keyframe
 * (translate + scale + fade, see motion.css); this helper only assigns the
 * per-item `animation-delay` and removes the class once played so re-renders
 * and state toggles do not replay it. The delay is capped so long feeds do
 * not queue seconds of entrance.
 *
 * Usage inside a <For>:
 *   <li ref={(el) => staggerItem(el, index())}>…</li>
 *
 * Reduced motion: the global CSS guard collapses the animation duration to
 * ~0, so items appear instantly (the delay only postpones a no-op).
 */

interface StaggerOptions {
  /** Delay between consecutive items (ms). @default 36 */
  stepMs?: number;
  /** Ceiling for the accumulated delay (ms). @default 320 */
  capMs?: number;
}

const DEFAULT_STEP_MS = 36;
const DEFAULT_CAP_MS = 320;
const ENTER_CLASS = "motion-item-enter";

/** Per-index delay with a hard cap — pure and unit-testable. */
export function staggerDelayMs(index: number, options: StaggerOptions = {}): number {
  const step = options.stepMs ?? DEFAULT_STEP_MS;
  const cap = options.capMs ?? DEFAULT_CAP_MS;
  return Math.min(Math.max(index, 0) * step, cap);
}

/**
 * Apply the staggered entrance to a list item. Call from a `ref` callback so
 * the class + delay are set before the element is connected (the animation
 * plays exactly once on insertion; `both` fill keeps it invisible during its
 * delay slot).
 */
export function staggerItem(el: HTMLElement, index: number, options?: StaggerOptions): void {
  el.style.animationDelay = `${staggerDelayMs(index, options)}ms`;
  el.classList.add(ENTER_CLASS);
  // NOT `{once:true}`: animationend bubbles, so a child's animation could pop
  // the listener before our own entrance finishes. Match the target instead.
  const onEnd = (event: AnimationEvent) => {
    if (event.target !== el) {
      return;
    }
    el.classList.remove(ENTER_CLASS);
    el.style.animationDelay = "";
    el.removeEventListener("animationend", onEnd);
  };
  el.addEventListener("animationend", onEnd);
}
